import React from "react";
import styled from "styled-components";
import { Colors, Icons } from "@sellout/ui";
import DetailsCard from "../elements/DetailsCard";
import { IEventGraphQL } from "@sellout/models/.dist/interfaces/IEvent";
import UPDATE_EVENT from "@sellout/models/.dist/graphql/mutations/updateEvent.mutation";
import { useMutation } from "@apollo/react-hooks";
import { useDispatch } from "react-redux";
import * as AppActions from "../redux/actions/app.actions";
import * as EventActions from "../redux/actions/event.actions";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import { getErrorMessage } from "@sellout/ui/build/utils/ErrorUtil";
import SelectImage, { SelectImageSizes } from "./SelectImage";
import useEvent from "../hooks/useEvent.hook";
import * as RemoveUtil from "../utils/RemoveUtil";

/* ─── Styled Components ─────────────────────────────────────────────────────── */

const Section = styled.div`
  margin-top: 12px;
`;

const PhotosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 12px;
`;

const LimitText = styled.div`
  color: ${Colors.Grey2};
  font-size: 1.2rem;
  font-weight: 500;
  margin-top: 8px;
  text-align: center;
`;

/* ─── Constants ──────────────────────────────────────────────────────────────── */

const MAX_ADDITIONAL_PHOTOS = 5;

const cleanEventForUpdate = (event: IEventGraphQL) => {
  let cleaned = { ...event } as any;
  cleaned = RemoveUtil.removeEmpty(cleaned);
  delete cleaned.organization;
  delete cleaned.fees;
  delete cleaned.artists;
  delete cleaned.createdAt;
  delete cleaned.venue;
  delete cleaned.webFlowEntity;
  delete cleaned.hasOrders;
  delete cleaned.published;
  delete cleaned.analytics;
  delete cleaned.seatingPublicKey;
  delete cleaned.active;
  delete cleaned.subscription;
  delete cleaned.guestList;
  return cleaned;
};

/* ─── Component ─────────────────────────────────────────────────────────────── */

const EventPhotosCard: React.FC = () => {
  const dispatch = useDispatch();
  const { event } = useEvent();

  const additionalPhotos: string[] = event?.additionalPhotos || [];
  const isLimitReached = additionalPhotos.length >= MAX_ADDITIONAL_PHOTOS;

  const [updateEvent] = useMutation(UPDATE_EVENT, {
    onCompleted(data) {
      if (data?.updateEvent) {
        dispatch(EventActions.cacheEvents([data.updateEvent as IEventGraphQL]));
      }
    },
    onError(error) {
      dispatch(
        AppActions.showNotification(
          getErrorMessage(error),
          AppNotificationTypeEnum.Error
        )
      );
    },
  });

  const savePhotos = (updatedPhotos: string[]) => {
    if (!event) return;
    const cleanedEvent = cleanEventForUpdate(event as IEventGraphQL);
    updateEvent({
      variables: {
        event: {
          ...cleanedEvent,
          additionalPhotos: updatedPhotos,
        },
      },
    });
  };

  const handleAddPhoto = (newPhotoUrl: string) => {
    if (!newPhotoUrl) return;
    if (additionalPhotos.length >= MAX_ADDITIONAL_PHOTOS) {
      dispatch(
        AppActions.showNotification(
          `Maximum limit of ${MAX_ADDITIONAL_PHOTOS} additional photos reached.`,
          AppNotificationTypeEnum.Error
        )
      );
      return;
    }

    savePhotos([...additionalPhotos, newPhotoUrl]);
  };

 
  const handleUpdatePhoto = (idx: number, url: string) => {
    let updatedPhotos: string[];

    if (!url) {
      updatedPhotos = additionalPhotos.filter((_, i) => i !== idx);
    } else {
      updatedPhotos = additionalPhotos.map((p, i) => (i === idx ? url : p));
    }

    savePhotos(updatedPhotos);
  };

  return (
    <DetailsCard
      title={`Additional Photos (${additionalPhotos.length}/${MAX_ADDITIONAL_PHOTOS})`}
      headerIcon={Icons.UploadLight}
      width="340px"
      padding="16px"
    >
      {additionalPhotos.length > 0 && (
        <PhotosGrid>
          {additionalPhotos.map((photoUrl: string, idx: number) => (
            <SelectImage
              key={idx}
              imageUrl={photoUrl}
              setImageUrl={(url) => handleUpdatePhoto(idx, url)}
              size={SelectImageSizes.Regular}
            />
          ))}
        </PhotosGrid>
      )}

      {!isLimitReached ? (
        <Section>
          <SelectImage
            setImageUrl={handleAddPhoto}
            size={SelectImageSizes.Regular}
            label="Upload Photo"
          />
        </Section>
      ) : (
        <LimitText>
          Maximum photo limit reached ({MAX_ADDITIONAL_PHOTOS}/{MAX_ADDITIONAL_PHOTOS})
        </LimitText>
      )}
    </DetailsCard>
  );
};

export default EventPhotosCard;